import { SearchOutlined } from '@ant-design/icons';
import { Checkbox, Empty, Input } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import classnames from 'classnames';
import { isEmpty } from 'lodash-es';
import React, { useMemo, useState } from 'react';
import { ReportGuideType } from '@/config/app/auth';
import { ReportScene } from '@/infrastructure/reporter/datong/report';
import { FormEntry } from '@/presentation/components/form';
import FreeProductGuide, { Display, GuideType } from '@/presentation/components/free-product-guide';
import {
  FORM_EXCEPTION_INFORMATION,
} from '@/presentation/components/tds-form/tds-fields-filter/config';
import { useStores } from '@/presentation/store';
import { ProductType } from '@/service/product-permissions';
import { FormColumnGroup } from '../types';


export interface IFilterArrayProps {
  entries?: FormEntry[];
  defaultSelectedEntries: FormEntry[];
  onSelectedChange?: (entries: FormEntry[]) => void;
  renderByGroup?: boolean;
}

export const COLUMN_GROUP_INFO = {
  [FormColumnGroup.UserInfo]: {
    title: '用户信息',
  },
  [FormColumnGroup.ApplicationInfo]: {
    title: '应用信息',
  },
  [FormColumnGroup.DeviceInfo]: {
    title: '设备信息',
  },
  [FormColumnGroup.ContextInfo]: {
    title: '现场信息',
  },
  [FormColumnGroup.exceptionInfo]: {
    title: '异常信息',
  },
  [FormColumnGroup.Tag]: {
    title: '平台标签',
  },
  [FormColumnGroup.Custom]: {
    title: '自定义数据',
  },
};

export const FilterArray = (props: IFilterArrayProps) => {
  const { entries = [], defaultSelectedEntries = [], onSelectedChange } = props;
  const { isFreeProduct } = useStores().ProductStore;
  const isFree = isFreeProduct();
  const [selectedEntries, setSelectedEntries] = useState<FormEntry[]>(isFree
    ? defaultSelectedEntries.filter(item => item?.formType?.includes?.(ProductType.FREE_PRODUCT))
    : defaultSelectedEntries);
  const [search, setSearch] = useState<string>('');
  // 免费
  const freeEntries = entries.filter(item => (isFree ? item?.formType?.includes?.(ProductType.FREE_PRODUCT) : true));
  const [selectAllChecked, setSelectAllChecked] = useState(selectedEntries.length >= freeEntries.length);

  const isChecked = (entry: FormEntry) => selectedEntries
    .some((curEntry: FormEntry) => entry.name === curEntry.name);

  const indeterminate = selectedEntries.length > 0 && selectedEntries.length < freeEntries.length;

  const handleSelectAllChange = (e: CheckboxChangeEvent) => {
    const { checked } = e.target;
    setSelectAllChecked(checked);
    const tempSelectedEntries = checked ? freeEntries : [];
    setSelectedEntries?.(tempSelectedEntries);
    onSelectedChange?.(tempSelectedEntries);
  };

  // 异常信息
  const exceptionInformationList = useMemo(
    () => {
      const exceptionList = entries
        ?.filter((item) => {
          const label = item.labelString || (typeof (item.label) === 'string' ? item.label : '') || '';
          return FORM_EXCEPTION_INFORMATION.includes(label) && label.indexOf(search || '') > -1;
        });
      const otherList = entries
        ?.filter((item) => {
          const label = item.labelString || (typeof (item.label) === 'string' ? item.label : '') || '';
          return (!FORM_EXCEPTION_INFORMATION.includes(label))
            && (label).indexOf(search || '') > -1;
        }) || [];
      if (isFree) {
        return exceptionList?.concat(otherList).sort((a, b) => {
          if (a.formType?.includes(ProductType.FREE_PRODUCT)) {
            return -1;
          }
          if (b.formType?.includes(ProductType.FREE_PRODUCT)) {
            return 1;
          }
          return 0;
        }) || [];
      }
      return exceptionList?.concat(otherList);
    },
    [entries, search, isFree],
  );

  const searchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const onClick = (entry: FormEntry, checked: boolean) => {
    const tempSelectedEntries = [...selectedEntries];

    if (checked) {
      tempSelectedEntries.push(entry);
    } else {
      const filterIndex = tempSelectedEntries.findIndex((curFilter: FormEntry) => entry.name === curFilter.name);
      if (filterIndex !== -1) {
        tempSelectedEntries.splice(filterIndex, 1);
      }
    }
    setSelectAllChecked(tempSelectedEntries.length >= freeEntries.length);
    setSelectedEntries(tempSelectedEntries);
    onSelectedChange?.(tempSelectedEntries);
  };

  const renderColumnGroup = () => {
    if (isEmpty(exceptionInformationList)) return <Empty description='暂无数据' />;

    const groups = Object.keys(COLUMN_GROUP_INFO);

    return (
        <div>
          {groups.map((group) => {
            const currentGroup = COLUMN_GROUP_INFO[group as FormColumnGroup];
            const groupColumn = exceptionInformationList.filter((column: FormEntry) => column.group === group);

            if (isEmpty(groupColumn)) return null;

            return (
              <div className='mb-[16px]'>
                <div className='text-xs mb-[4px]'>{currentGroup.title}</div>
                <div className={'flex gap-[8px] flex-wrap'}>{groupColumn.map((item, index) => {
                  const isSelect = isChecked(item);
                  const openGuide = isFree && !item?.formType?.includes?.(ProductType.FREE_PRODUCT);
                  return (
                    <FreeProductGuide openGuide={openGuide}
                      guideType={GuideType.POP_CONFIRM} display={Display.BLOCK}
                      reportGuideType={ReportGuideType.Bubble}
                      reportScene={ReportScene.FilterArray}
                      reportField={item?.labelString || item?.label as string}
                    >
                      <div key={index} onClick={() => onClick(item, !isSelect)} className={
                        classnames({
                          ['text-[12px] px-[8px] py-[2px] rounded-[2px] cursor-pointer']: true,
                          ['text-[#4A5970] bg-[#EDF1F7]']: !isSelect,
                          ['text-[#FFF] bg-[#0A6CFF]']: isSelect,
                        })}>{item?.labelString || item?.label}
                      </div>
                    </FreeProductGuide>
                  );
                })}</div>
              </div>
            );
          })}
        </div>
    );
  };

  const renderColumns = () => (exceptionInformationList?.length
    ?        <div className={'flex gap-[8px] flex-wrap pb-[12px]'}>{exceptionInformationList.map((item, index) => {
      const isSelect = isChecked(item);
      const openGuide = isFree && !item?.formType?.includes?.(ProductType.FREE_PRODUCT);
      return <FreeProductGuide openGuide={openGuide}
                                 guideType={GuideType.POP_CONFIRM} display={Display.BLOCK}
                                 reportGuideType={ReportGuideType.Bubble}
                                 reportScene={ReportScene.FilterArray}
                                 reportField={item?.labelString || item?.label as string}>
            <div key={index} onClick={() => onClick(item, !isSelect)} className={
            classnames({
              ['text-[12px] px-[8px] py-[2px] rounded-[2px] cursor-pointer']: true,
              ['text-[#4A5970] bg-[#EDF1F7]']: !isSelect,
              ['text-[#FFF] bg-[#0A6CFF]']: isSelect,
              ['text-[#A1ACBD]']: openGuide,
            })}>{item?.labelString || item?.label}</div>
          </FreeProductGuide>;
    })}</div> : null);

  return <div
  className={'flex flex-col px-[12px]'}
  style={props.renderByGroup ? { width: 588 } : { width: 300 }}>
    <div className='mb-[8px]'>
      <Input value={search} onChange={searchChange} placeholder={'搜索字段'} suffix={<SearchOutlined />} />
    </div>
    {props.renderByGroup ? renderColumnGroup() : renderColumns()}
    <div className={'pt-[8px] border-t border-[#EDF1F7] border-solid flex justify-between'}>
      {!search ? <Checkbox
        checked={selectAllChecked}
        onChange={handleSelectAllChange}
        indeterminate={indeterminate}
      >全选</Checkbox> : <div />}
    </div>
  </div>;
};
